/**
 * HTML-Patching fuer die ogProxy Cloud Function: ersetzt <title> und
 * <meta name="description"> der SPA-Shell, injiziert einen
 * OG-/Twitter-Tag-Block vor </head> und (optional) Canonical- +
 * hreflang-Link-Tags vor dem alternate-hreflang-placeholder-Meta.
 *
 * Sicherheits-Invariante: `patchShellHtml` ist der EINZIGE Weg, dynamische
 * Werte (Poll-Titel etc. = User-Content) ins HTML zu bringen — alles wird
 * hier via `escapeHtml` escaped (XSS). Aus der Shell selbst extrahierte
 * Werte sind bereits entity-encoded und werden unveraendert wiederverwendet
 * (kein Doppel-Escape).
 */

export function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const TITLE_TAG = /<title>([^<]*)<\/title>/i;
const DESCRIPTION_TAG = /<meta\s[^>]*name="description"[^>]*>/i;
const CONTENT_ATTRIBUTE = /content="([^"]*)"/i;
const OG_TITLE_TAG = /<meta\s[^>]*property="og:title"/i;
/**
 * Anker fuer Canonical/hreflang: dasselbe Placeholder-Meta hoch im <head>,
 * vor dem auch der Angular-Client seine Tags einfuegt (insertElement in
 * RouteContextService) — Server- und Client-Tags landen so an derselben Stelle.
 */
const HREFLANG_PLACEHOLDER = /<meta\s[^>]*id="alternate-hreflang-placeholder"[^>]*>/i;
/** Schon vorhandene Canonical-/hreflang-Links -> nicht doppelt injizieren. */
const SEO_LINK_TAG = /<link\s[^>]*(rel="canonical"|hreflang=)/i;
const HEAD_CLOSE = '</head>';

export interface ShellMeta {
    /** Entity-encoded, wie im Shell-HTML — nicht erneut escapen. */
    title?: string;
    description?: string;
}

/** Liest bestehenden <title> + Description aus der Shell (fuer den generischen Branch). */
export function extractShellMeta(shellHtml: string): ShellMeta {
    const result: ShellMeta = {};
    const titleMatch = TITLE_TAG.exec(shellHtml);
    if (titleMatch) {
        result.title = titleMatch[1];
    }
    const descriptionTag = DESCRIPTION_TAG.exec(shellHtml);
    if (descriptionTag) {
        const contentMatch = CONTENT_ATTRIBUTE.exec(descriptionTag[0]);
        if (contentMatch) {
            result.description = contentMatch[1];
        }
    }
    return result;
}

export interface OgPatch {
    /** Plain text (unescaped). Weggelassen -> Shell-Title/-Description bleiben und liefern die OG-Werte. */
    title?: string;
    description?: string;
    /** Absolute URL. */
    ogUrl: string;
    /** Absolute URL, >=1200px breit fuer summary_large_image. */
    ogImage: string;
    /**
     * Pixel-Masse des og:image — wenn beide gesetzt, werden og:image:width/height
     * mitgeschrieben (FB/WhatsApp rendern die Card dann schon beim ERSTEN Share
     * gross, statt das Bild erst asynchron zu vermessen).
     */
    ogImageWidth?: number;
    ogImageHeight?: number;
    siteName: string;
    /**
     * Absolute URL fuer <link rel="canonical">. Weggelassen -> kein Tag
     * (Vote-/generischer Zweig: dort gibt es bewusst keinen Canonical).
     */
    canonical?: string;
    /**
     * hreflang-Alternates (6 Sprachen + `x-default`), absolute URLs.
     * Weggelassen -> keine Tags.
     */
    alternates?: { hreflang: string; href: string }[];
}

/** Baut die Canonical-/hreflang-Link-Tags aus dem Patch (leerer String, wenn keine Felder). */
function buildSeoLinkTags(patch: OgPatch): string {
    let tags = '';
    if (patch.canonical) {
        tags += '<link rel="canonical" href="' + escapeHtml(patch.canonical) + '">';
    }
    if (patch.alternates) {
        for (const alternate of patch.alternates) {
            tags +=
                '<link rel="alternate" hreflang="' + escapeHtml(alternate.hreflang) +
                '" href="' + escapeHtml(alternate.href) + '">';
        }
    }
    return tags;
}

/**
 * Patcht die Shell. Fail-closed: ohne </head> kommt die Shell unveraendert
 * zurueck; ist schon ein og:title vorhanden (bereits gepatcht), wird kein
 * zweiter Block injiziert (Idempotenz).
 */
export function patchShellHtml(shellHtml: string, patch: OgPatch): string {
    if (!shellHtml || shellHtml.indexOf(HEAD_CLOSE) < 0) {
        return shellHtml;
    }

    let html = shellHtml;

    // --- <title> + Description ersetzen (nur wenn dynamische Werte da sind) ---
    const escapedTitle = patch.title !== undefined ? escapeHtml(patch.title) : undefined;
    const escapedDescription = patch.description !== undefined ? escapeHtml(patch.description) : undefined;
    if (escapedTitle !== undefined) {
        html = html.replace(TITLE_TAG, '<title>' + escapedTitle + '</title>');
    }
    if (escapedDescription !== undefined) {
        const descriptionTag = DESCRIPTION_TAG.exec(html);
        if (descriptionTag) {
            const patchedTag = descriptionTag[0].replace(
                CONTENT_ATTRIBUTE,
                'content="' + escapedDescription + '"'
            );
            html = html.replace(descriptionTag[0], patchedTag);
        }
    }

    // --- Canonical + hreflang injizieren (hoch im <head>, vor dem Placeholder —
    // dieselbe Stelle, an der der Angular-Client sie einfuegen wuerde) ---
    const seoLinkTags = buildSeoLinkTags(patch);
    if (seoLinkTags && !SEO_LINK_TAG.test(html)) {
        const placeholderMatch = HREFLANG_PLACEHOLDER.exec(html);
        if (placeholderMatch) {
            html = html.replace(placeholderMatch[0], seoLinkTags + placeholderMatch[0]);
        } else {
            // Fail-safe: Placeholder fehlt in der Shell -> ans Head-Ende
            html = html.replace(HEAD_CLOSE, seoLinkTags + HEAD_CLOSE);
        }
    }

    // --- OG-/Twitter-Block injizieren ---
    if (OG_TITLE_TAG.test(html)) {
        return html; // schon gepatcht -> kein Duplikat-Block
    }
    const shellMeta = extractShellMeta(html);
    // Dynamische Werte sind frisch escaped; Shell-Werte sind schon encoded.
    const ogTitle = escapedTitle !== undefined ? escapedTitle : shellMeta.title || '';
    const ogDescription =
        escapedDescription !== undefined ? escapedDescription : shellMeta.description || '';
    const ogUrl = escapeHtml(patch.ogUrl);
    const ogImage = escapeHtml(patch.ogImage);
    const siteName = escapeHtml(patch.siteName);

    const ogImageDimensions =
        patch.ogImageWidth && patch.ogImageHeight
            ? '<meta property="og:image:width" content="' + patch.ogImageWidth + '">' +
              '<meta property="og:image:height" content="' + patch.ogImageHeight + '">'
            : '';

    const ogBlock =
        '<meta property="og:title" content="' + ogTitle + '">' +
        '<meta property="og:description" content="' + ogDescription + '">' +
        '<meta property="og:type" content="website">' +
        '<meta property="og:url" content="' + ogUrl + '">' +
        '<meta property="og:site_name" content="' + siteName + '">' +
        '<meta property="og:image" content="' + ogImage + '">' +
        ogImageDimensions +
        '<meta name="twitter:card" content="summary_large_image">' +
        '<meta name="twitter:title" content="' + ogTitle + '">' +
        '<meta name="twitter:description" content="' + ogDescription + '">' +
        '<meta name="twitter:image" content="' + ogImage + '">';

    return html.replace(HEAD_CLOSE, ogBlock + HEAD_CLOSE);
}
